<template>
  <v-row align="center">
    <v-col
      cols="12"
      :xl="gridSpacing.left.xl"
      :lg="gridSpacing.left.lg"
      :md="gridSpacing.left.md"
      :sm="gridSpacing.left.sm"
    >
      <chart-card
        :title="$t(title)"
        :subtitle="subtitle"
        :is-loaded="isLoaded"
        :has-error="hasError"
        :theme-data="data"
        :error-msg="errorMsg"
        :error-empty="errorEmpty"
        :color="color"
        :labels="labels"
        :manual-ticks-limit="false"
      />
    </v-col>
    <v-col
      cols="12"
      :xl="gridSpacing.right.xl"
      :lg="gridSpacing.right.lg"
      :md="gridSpacing.right.md"
      :sm="gridSpacing.right.sm"
    >
      <stats-learning-progress
        :request="request"
        :is-admin="isAdmin"
        :error-msg="errorMsg"
        :error-empty="errorEmpty"
        :color="color"
        @set-error="$emit('set-error', $event)"
      />
    </v-col>
  </v-row>
</template>

<script>
  /* Les composants et packages dont on a besoin */
  import ChartCard from '../../charts/ChartCard.vue'
  import StatsLearningProgress from '../../stats/StatsLearningProgress.vue'
  import moment from 'moment'

  /* Rend le composant accessible aux autres composants */
  export default {
    /* Nom du composant */
    name: 'LearningProgress',

    /* Sous-composants utilisés par ce composant */
    components: {
      ChartCard,
      StatsLearningProgress,
    },

    /* Paramètres */
    props: {
      request: {
        type: String,
        default: '',
      },
      isAdmin: {
        type: Boolean,
        default: false,
      },
      errorMsg: {
        type: String,
        default: '',
      },
      errorEmpty: {
        type: Object,
        default: function () {
          return {
            msg: 'Un problème est survenu.',
            icon: 'mdi-alert-rhombus-outline',
            color: 'error',
          }
        },
      },
      color: {
        type: String,
        default: 'primary',
      },
    },

    /* Variables internes
     * data doit être une fonction si ce composant est utilisé dans un autre composant */
    data: function () {
      return {
        gridSpacing: {
          left: {
            xl: '7',
            lg: '7',
            md: '12',
            sm: '12',
            xs: '12',
          },
          right: {
            xl: '5',
            lg: '5',
            md: '12',
            sm: '12',
            xs: '12',
          },
        },
        title: 'Training.ChartEvolution.Title',
        subtitle: undefined,
        isLoaded: false,
        hasError: false,
        rawData: {
          Challenges: {
            labels: [],
            datasets: [{
              data: undefined,
            }],
          },
          Quizz: {
            labels: [],
            datasets: [{
              data: undefined,
            }],
          },
        },
        dataDates: [],
        labels: {
          x: 'Semaines',
          y: 'Nombre',
        },
      }
    },

    /* Variables internes dynamiques
     * (MAJ automatique quand les variables qu'elles utilisent sont mises à jour) */
    computed: {
      // Génération des données au format chartjs
      data: function () {
        // Crée un clône des labels, les trie puis les renomme
        const labels = this.dataDates.map(x => x).sort().map(x => this.formatDate(x))
        const colors = this.getColors(Object.keys(this.rawData).length)
        let colorIterator = 0
        const datasets = []
        if (this.rawData) {
          Object.keys(this.rawData).forEach(property => {
            // Création d'un deep clone pour ne pas impacter this.rawData par effet de bord
            const mutatedDatas = this.deepCopy(this.rawData[property].datasets[0])
            const tmpData = []
            Object.keys(labels).forEach(date => {
              const index = (this.rawData[property].labels
                ? this.rawData[property].labels.findIndex(x => x === labels[date])
                : -1)

              /* Si la date existante n'a pas de données pour cette courbe (index === -1),
                 alors on récupère l'ancienne valeur ou 0 s'il n'y en a pas
                 Sinon, récupère la valeur cumulée (nouvelle + ancienne ou 0 si inexistante) */
              tmpData.push(
                (index === -1 ? 0 : this.rawData[property].datasets[0].data[index]) +
                  (tmpData.length > 0 ? tmpData[tmpData.length - 1] : 0),
              )
            })
            if (this.rawData[property].datasets[0]) {
              // On affecte le nouveau tableau de valeurs cumulées
              mutatedDatas.data = tmpData

              // On affecte les couleurs en fonctions du type
              mutatedDatas.backgroundColor = colors.colors[colorIterator]
              mutatedDatas.borderColor = colors.borderColors[colorIterator]
              datasets.push(mutatedDatas)
              colorIterator++
            }
          })
        }
        return {
          labels: labels,
          datasets: datasets,
        }
      },
    },

    /* Variables surveillées : traitement si modification de sa valeur */
    watch: {
      request: function () {
        this.isLoaded = false
        this.fetchAllData()
      },
    },

    /* Hook : fonction appelée juste après que l’instance ait été montée */
    mounted: function () {
      this.fetchAllData()
    },

    /* Fonctions internes */
    methods: {
      deepCopy: function (inObject) {
        let value, key

        if (typeof inObject !== 'object' || inObject === null) {
          return inObject // Return the value if inObject is not an object
        }

        // Create an array or object to hold the values
        const outObject = Array.isArray(inObject) ? [] : {}

        for (key in inObject) {
          value = inObject[key]

          // Recursively (deep) copy for nested objects, including arrays
          outObject[key] = this.deepCopy(value)
        }

        return outObject
      },

      fetchAllData: function () {
        this.dataDates = []
        this.lineChartData('Challenges')
        this.lineChartData('Quizz')
      },

      // Met la date au bon format
      formatDate: function (date) {
        if (date) {
          return 'S' + moment(date, 'YYYYW').format('W (MM/YYYY)').toString()
        }
      },

      // Génération des couleurs pour les courbes
      getColors: function (lvlCount) {
        const colors = []
        const borderColors = []
        this.$chartColors.forEach((item, i) => {
          if (i < lvlCount) {
            colors.push('rgba(' + item.r + ',' + item.g + ',' + item.b + ', 0.4)')
            borderColors.push('rgba(' + item.r + ',' + item.g + ',' + item.b + ', 1)')
          }
        })
        return { colors: colors, borderColors: borderColors }
      },

      // Récupération des données brutes
      lineChartData: function (type) {
        let url = ''
        switch (type) {
          case 'Challenges':
            url = 'ChallengeSucceedByWeek'
            break
          case 'Quizz':
            url = 'QuizzSucceedByWeek'
            break
        }
        this.$Auth.httpRequest(
          'get',
          'api/dashboard/evolution' + url + this.request,
        ).then(datas => {
          let labelsCustom = []
          const dates = []
          const datasetsCustom = []
          const value = []

          Object.keys(datas.data).forEach(key => {
            const week = datas.data[key].week ? datas.data[key].week.toString() : ''
            const formattedDate = this.formatDate(week)

            // const previousValue = key > 0 ? value[key - 1] : 0
            // value.push(datas.data[key].count + previousValue)
            value.push(datas.data[key].count)

            const index = dates.findIndex(x => x === formattedDate)
            if (index === -1) {
              dates.push(formattedDate)
            }

            const indexGlobal = this.dataDates.findIndex(x => x === week)
            if (indexGlobal === -1) {
              this.dataDates.push(week)
            }

            //  on assigne au label custom les dates
            labelsCustom = dates
          })

          datasetsCustom.push({
            label: type,
            fill: false,
            lineTension: 0.1,
            borderCapStyle: 'butt',
            borderJoinStyle: 'miter',
            data: value,
          })

          this.isLoaded = true
          this.rawData[type] = {
            labels: labelsCustom,
            datasets: datasetsCustom,
          }
          this.hasError = false
        }).catch((error) => {
          this.rawData[type] = {
            labels: [],
            datasets: [],
          }
          if (this.isAdmin) {
            // console.log('Error in ' + this.$options.name + ' : ', error)
          }
          this.hasError = true
          this.$emit('set-error', error)
        })
      },

    },
  }
</script>
